import { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import { MoveHorizontal, ArrowLeftRight, Files, Trash2, Plus, Settings, Check, ChevronsUpDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/budget-utils';
import type { CostBudget, CostCategory, ResourceBudget } from '@/types/budget.types';
import type { GlobalCategory } from '@/types/category.types';
import { fetchGlobalCategories, getDataMode } from '@/lib/data-service';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { FillSheet } from './fill-sheet';
import { ShiftBudgetSheet } from './shift-budget-sheet';
import { AISuggestion } from './ai-suggestion';
import type { FillType } from './fill-dialog';

interface EditCostBudgetFormProps {
  costBudget: CostBudget;
  onChange: (changes: CostBudget) => void;
  selectedYear?: string;
  projectStartDate?: string;
  projectEndDate?: string;
  availableCategories: CostCategory[];
  resourceBudget?: ResourceBudget; // Used to auto-calculate Labour category
}

// Month names for columns
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Mock data: Role rates (£/hour)
// In production, this would come from the backend API
const ROLE_RATES: Record<string, number> = {
  'Project Manager': 200,
  'Senior Consultant': 180,
  'Consultant': 150,
  'Developer': 150,
  'Senior Developer': 175,
  'QA Analyst': 125,
  'QA Engineer': 125,
  'Business Analyst': 140,
  'Technical Architect': 220,
  'UX Designer': 160,
  'Scrum Master': 175,
};

// Mock data: Resource Budget (hours per role per month)
// In production, this would come from the Resource Budget API
interface MockResourceBudget {
  role: string;
  rate: number;
  monthlyHours: Record<string, number>; // monthKey -> hours
}

const MOCK_RESOURCE_BUDGET: MockResourceBudget[] = [
  {
    role: 'Project Manager',
    rate: ROLE_RATES['Project Manager'],
    monthlyHours: {
      '2024-05': 80,
      '2024-06': 80,
      '2024-07': 80,
      '2024-08': 80,
      '2024-09': 80,
      '2024-10': 80,
      '2024-11': 80,
      '2024-12': 80,
      '2025-01': 80,
    },
  },
  {
    role: 'Senior Developer',
    rate: ROLE_RATES['Senior Developer'],
    monthlyHours: {
      '2024-05': 160,
      '2024-06': 160,
      '2024-07': 160,
      '2024-08': 160,
      '2024-09': 160,
      '2024-10': 160,
      '2024-11': 160,
      '2024-12': 160,
      '2025-01': 160,
    },
  },
  {
    role: 'Developer',
    rate: ROLE_RATES['Developer'],
    monthlyHours: {
      '2024-05': 160,
      '2024-06': 160,
      '2024-07': 160,
      '2024-08': 160,
      '2024-09': 160,
      '2024-10': 160,
      '2024-11': 160,
      '2024-12': 160,
      '2025-01': 160,
    },
  },
  {
    role: 'QA Engineer',
    rate: ROLE_RATES['QA Engineer'],
    monthlyHours: {
      '2024-05': 80,
      '2024-06': 80,
      '2024-07': 80,
      '2024-08': 80,
      '2024-09': 80,
      '2024-10': 80,
      '2024-11': 80,
      '2024-12': 80,
      '2025-01': 80,
    },
  },
];

// Helper function to calculate Labour budget from Resource Budget
// Labour = Sum of (hours × rate) for all roles in that month
const calculateLabourBudget = (monthKey: string): number => {
  return MOCK_RESOURCE_BUDGET.reduce((total, resource) => {
    const hours = resource.monthlyHours[monthKey] || 0;
    return total + (hours * resource.rate);
  }, 0);
};

// Mock data: Initial budget values for non-Labour categories
const MOCK_INITIAL_BUDGETS: Record<string, Record<string, number>> = {
  'Materials': {
    '2024-05': 5000,
    '2024-06': 3000,
    '2024-07': 2000,
    '2024-08': 2000,
    '2024-09': 1500,
    '2024-10': 1500,
    '2024-11': 1000,
    '2024-12': 1000,
    '2025-01': 500,
  },
  'Equipment': {
    '2024-05': 15000,
    '2024-06': 2000,
    '2024-07': 1000,
    '2024-08': 1000,
    '2024-09': 1000,
    '2024-10': 1000,
    '2024-11': 500,
    '2024-12': 500,
    '2025-01': 500,
  },
  'Subcontractors': {
    '2024-05': 0,
    '2024-06': 0,
    '2024-07': 10000,
    '2024-08': 10000,
    '2024-09': 10000,
    '2024-10': 10000,
    '2024-11': 0,
    '2024-12': 0,
    '2025-01': 0,
  },
  'Travel & Expenses': {
    '2024-05': 1500,
    '2024-06': 1500,
    '2024-07': 1500,
    '2024-08': 1500,
    '2024-09': 1500,
    '2024-10': 1500,
    '2024-11': 1500,
    '2024-12': 1500,
    '2025-01': 1500,
  },
  'Software & Licenses': {
    '2024-05': 5000,
    '2024-06': 500,
    '2024-07': 500,
    '2024-08': 500,
    '2024-09': 500,
    '2024-10': 500,
    '2024-11': 500,
    '2024-12': 500,
    '2025-01': 500,
  },
  'Overhead': {
    '2024-05': 3000,
    '2024-06': 3000,
    '2024-07': 3000,
    '2024-08': 3000,
    '2024-09': 3000,
    '2024-10': 3000,
    '2024-11': 3000,
    '2024-12': 3000,
    '2025-01': 3000,
  },
  'Contingency': {
    '2024-05': 5000,
    '2024-06': 5000,
    '2024-07': 5000,
    '2024-08': 5000,
    '2024-09': 5000,
    '2024-10': 5000,
    '2024-11': 5000,
    '2024-12': 5000,
    '2025-01': 5000,
  },
};

// NumberInput component that handles 2dp formatting properly with max value validation
const NumberInput = ({ value, onChange, maxValue = 999999.99, onFocus, disabled, ...props }: { value: number; onChange: (value: number) => void; maxValue?: number; onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void; disabled?: boolean; [key: string]: any }) => {
  const [displayValue, setDisplayValue] = useState(value.toFixed(2));
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(value.toFixed(2));
    }
  }, [value, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    setIsFocused(true);
    // Select all text on focus for easy editing
    e.target.select();
    // Call parent's onFocus if provided
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    // Clear any pending debounced update
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    let numValue = parseFloat(displayValue) || 0;
    
    // Enforce maximum value
    if (numValue > maxValue) {
      numValue = maxValue;
    }
    
    const formattedValue = parseFloat(numValue.toFixed(2));
    setDisplayValue(formattedValue.toFixed(2));
    // Only call onChange if the value actually changed to avoid false "unsaved changes"
    if (formattedValue !== value) {
      onChange(formattedValue);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const val = e.target.value;
    // Allow typing numbers, decimal point, and empty string
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setDisplayValue(val);
      
      // Debounced onChange to enable toolbar buttons while typing
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      // Set a new debounced timer (300ms delay to allow for slow typing)
      debounceTimerRef.current = setTimeout(() => {
        let numValue = parseFloat(val) || 0;
        // Enforce maximum value
        if (numValue > maxValue) {
          numValue = maxValue;
        }
        const formattedValue = parseFloat(numValue.toFixed(2));
        // Only call onChange if the value actually changed
        if (formattedValue !== value) {
          onChange(formattedValue);
        }
      }, 300);
    }
  };

  return (
    <Input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
    />
  );
};

// Helper function to format numbers with thousand separators
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Category type display names and ordering for grouped combobox
const CATEGORY_TYPE_LABELS: Record<string, string> = {
  labour: 'Labour',
  expenses: 'Expenses',
  subcontractors: 'Subcontractors',
  overhead: 'Overhead',
  contingency: 'Contingency',
};
const CATEGORY_TYPE_ORDER = ['labour', 'expenses', 'subcontractors', 'overhead', 'contingency'];

// Extended category interface with monthly budgets as array for form display
interface CategoryBudgetRow extends Omit<CostCategory, 'monthlyBudgets'> {
  monthlyBudgets: number[]; // Budget per month (array for form display, converted to/from Record for API)
}

export function EditCostBudgetForm({
  costBudget,
  onChange,
  selectedYear,
  projectStartDate,
  projectEndDate,
  availableCategories,
  resourceBudget,
}: EditCostBudgetFormProps) {
  // Calculate all available months for the project across all years
  const allProjectMonths = (() => {
    if (!projectStartDate || !projectEndDate) {
      // Show all months of current year if no project dates
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => ({ year: currentYear, month: i }));
    }
    
    const startDate = new Date(projectStartDate);
    const endDate = new Date(projectEndDate);
    
    const months: { year: number; month: number }[] = [];
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    
    while (current <= end) {
      months.push({ year: current.getFullYear(), month: current.getMonth() });
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  })();

  // Show ALL months for the entire project duration - no year filtering
  const yearMonths = allProjectMonths;

  // Debug logging
  console.log('[EditCostBudgetForm] Project dates:', { projectStartDate, projectEndDate });
  console.log('[EditCostBudgetForm] Total months to display:', yearMonths.length);
  console.log('[EditCostBudgetForm] Month range:', yearMonths.length > 0 ? `${MONTHS[yearMonths[0].month]} ${yearMonths[0].year} - ${MONTHS[yearMonths[yearMonths.length - 1].month]} ${yearMonths[yearMonths.length - 1].year}` : 'None');

  // Create a mapping from global month index to year/month
  const monthIndexMap = yearMonths.map((ym, index) => ({
    globalIndex: index,
    year: ym.year,
    month: ym.month,
    yearMonthKey: `${ym.year}-${ym.month}` // Unique key for this month
  }));

  // Show ALL months - no dynamic loading
  const visibleMonths = monthIndexMap.map(m => m.globalIndex);
  
  // Total number of months to display
  const totalMonths = yearMonths.length;

  // Check if we're in Live mode - don't use mock data in Live mode
  const isLiveMode = getDataMode() === 'live';

  // Helper function to calculate Labour budget from actual Resource Budget data
  // Labour = Sum of (hours × costRate) for all roles in that month
  const calculateLabourFromResourceBudget = (monthKey: string): number => {
    if (!resourceBudget?.byRole || resourceBudget.byRole.length === 0) {
      return 0;
    }
    
    return resourceBudget.byRole.reduce((total, role) => {
      // monthlyHours is an array indexed by month (0-11), we need to map monthKey to index
      // monthKey format: "2024-05" (year-month where month is 1-indexed)
      const [, monthStr] = monthKey.split('-');
      const monthIndex = parseInt(monthStr, 10) - 1; // Convert to 0-indexed
      
      // For now, use the role's total budget hours divided by project months as fallback
      // In a full implementation, this would use monthly allocations from the resource budget
      const hours = role.monthlyHours?.[monthIndex] || 0;
      const rate = role.billRate || 0;
      
      return total + (hours * rate);
    }, 0);
  };

  const buildCategoryRows = useCallback((cats: CostCategory[]): CategoryBudgetRow[] => {
    return cats.map(category => {
      // Create monthly budgets array
      const monthlyBudgets = monthIndexMap.map(({ year, month }) => {
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
        
        // First priority: Use saved monthly budgets from API if available
        if (category.monthlyBudgets && category.monthlyBudgets[monthKey] !== undefined) {
          return category.monthlyBudgets[monthKey];
        }
        
        // In Live mode: For Labour, calculate from actual Resource Budget; for others, return 0
        if (isLiveMode) {
          if (category.name === 'Labour' || category.type === 'labour') {
            return calculateLabourFromResourceBudget(monthKey);
          }
          return 0;
        }
        
        // Demo mode only: Labour category - Calculate from mock Resource Budget (hours × rate)
        if (category.name === 'Labour') {
          return calculateLabourBudget(monthKey);
        }
        
        // Demo mode only: Use mock initial budgets as fallback for new categories
        const categoryBudgets = MOCK_INITIAL_BUDGETS[category.name];
        return categoryBudgets?.[monthKey] || 0;
      });

      return {
        ...category,
        monthlyBudgets,
      };
    });
  }, [calculateLabourFromResourceBudget, isLiveMode, monthIndexMap]);

  // Initialize categories with properly sized monthlyBudgets arrays
  // In Live mode: Only use saved data from API, no mock fallbacks
  // In Demo mode: Use mock data as fallback for demonstration purposes
  const [categories, setCategories] = useState<CategoryBudgetRow[]>(() => buildCategoryRows(availableCategories));

  // Allow availableCategories to arrive async (e.g. fetched default GlobalCategories)
  useEffect(() => {
    if (categories.length === 0 && availableCategories.length > 0) {
      setCategories(buildCategoryRows(availableCategories));
    }
  }, [availableCategories, buildCategoryRows, categories.length]);

  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [selectedCell, setSelectedCell] = useState<{
    categoryIndex: number;
    monthIndex: number | null;
  } | null>(null);

  // Fill dialog state
  const [fillDialogOpen, setFillDialogOpen] = useState(false);
  const [fillSource, setFillSource] = useState<{ categoryIndex: number; monthIndex: number; value: number } | null>(null);

  // Shift budget dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftSource, setShiftSource] = useState<{ categoryIndex: number; categoryName: string } | null>(null);

  // Available global categories for inline combobox
  const [availableGlobalCategories, setAvailableGlobalCategories] = useState<GlobalCategory[]>([]);
  const [openCategoryPopover, setOpenCategoryPopover] = useState<number | null>(null);

  // Delete confirmation state
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; category: CategoryBudgetRow } | null>(null);

  // AI suggestion state for pattern detection
  const [patternSuggestion, setPatternSuggestion] = useState<{
    categoryIndex: number;
    value: number;
    fromMonth: number;
    toMonth: number;
  } | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  // Track if this is the initial mount to prevent marking form as dirty on load
  const isInitialMount = useRef(true);

  const navigate = useNavigate();

  // Track whether any global categories exist (for empty state messaging)
  const [hasGlobalCategories, setHasGlobalCategories] = useState<boolean | null>(null);

  const goToBudgetSettings = useCallback(() => {
    navigate('/settings?tab=budget-settings');
  }, [navigate]);

  // Load global categories on mount for inline combobox
  useEffect(() => {
    fetchGlobalCategories().then(globalCategories => {
      const activeCategories = globalCategories.filter(gc => gc.status === 'active');
      setAvailableGlobalCategories(activeCategories);
      setHasGlobalCategories(activeCategories.length > 0);
    }).catch(() => {
      setHasGlobalCategories(false);
    });
  }, []);

  useEffect(() => {
    // Skip onChange on initial mount to prevent false "unsaved changes" alert
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Calculate totals for each category and convert monthlyBudgets array to Record<string, number>
    const updatedCategories = categories.map(cat => {
      // Convert monthlyBudgets array to Record<string, number> for API storage
      const monthlyBudgetsRecord: Record<string, number> = {};
      cat.monthlyBudgets.forEach((value, index) => {
        if (monthIndexMap[index]) {
          const { year, month } = monthIndexMap[index];
          const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`; // Format: 2024-05
          monthlyBudgetsRecord[monthKey] = value;
        }
      });

      return {
        ...cat,
        budget: cat.monthlyBudgets.reduce((sum, val) => sum + val, 0),
        monthlyBudgets: monthlyBudgetsRecord,
      };
    });

    // Calculate overall totals
    const totalBudget = updatedCategories.reduce((sum, cat) => sum + cat.budget, 0);
    const totalActual = updatedCategories.reduce((sum, cat) => sum + cat.actual, 0);
    const totalCommitted = updatedCategories.reduce((sum, cat) => sum + cat.committed, 0);

    onChange({
      ...costBudget,
      categories: updatedCategories,
      totals: {
        budget: totalBudget,
        actual: totalActual,
        committed: totalCommitted,
        available: totalBudget - totalActual - totalCommitted,
        progressPercent: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
      },
    });
  }, [categories]);

  const updateMonthlyBudget = (categoryIndex: number, monthIndex: number, value: number) => {
    console.log('[updateMonthlyBudget] Called:', { categoryIndex, monthIndex, value, totalMonths });
    const updated = [...categories];
    console.log('[updateMonthlyBudget] Current category:', updated[categoryIndex]);
    
    // Ensure monthlyBudgets array is properly sized
    const existingBudgets = updated[categoryIndex].monthlyBudgets || [];
    console.log('[updateMonthlyBudget] Existing budgets:', existingBudgets);
    const monthlyBudgets = existingBudgets.length >= totalMonths 
      ? [...existingBudgets]
      : [...existingBudgets, ...Array(totalMonths - existingBudgets.length).fill(0)];
    console.log('[updateMonthlyBudget] Sized monthlyBudgets:', monthlyBudgets);
    
    monthlyBudgets[monthIndex] = value;
    console.log('[updateMonthlyBudget] After update:', monthlyBudgets);
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    
    // Pattern detection for AI suggestions
    detectPattern(categoryIndex, monthIndex, value, monthlyBudgets);
  };

  // Pattern detection for AI suggestions
  const detectPattern = (categoryIndex: number, monthIndex: number, value: number, monthlyBudgets: number[]) => {
    // Don't suggest if already dismissed
    const suggestionKey = `pattern-${categoryIndex}`;
    if (dismissedSuggestions.has(suggestionKey)) return;
    
    // Check if last 3 values are the same and non-zero
    if (monthIndex >= 2 && value > 0) {
      const last3 = [
        monthlyBudgets[monthIndex - 2],
        monthlyBudgets[monthIndex - 1],
        monthlyBudgets[monthIndex]
      ];
      
      const allSame = last3.every(v => v === value);
      
      if (allSame && monthIndex < totalMonths - 1) {
        // Show suggestion to fill remaining months
        setPatternSuggestion({
          categoryIndex,
          value,
          fromMonth: monthIndex + 1,
          toMonth: totalMonths - 1
        });
      }
    }
  };

  // Row selection handlers
  const toggleRowSelection = (categoryId: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(categoryId)) {
      newSelection.delete(categoryId);
    } else {
      newSelection.add(categoryId);
    }
    setSelectedRows(newSelection);
    // Clear cell selection when selecting a row
    setSelectedCell(null);
  };

  const toggleAllRows = () => {
    if (selectedRows.size === categories.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(categories.map(cat => cat.id)));
    }
    // Clear cell selection when selecting all rows
    setSelectedCell(null);
  };

  // Fill handlers
  const handleFillClick = (categoryIndex: number, monthIndex: number) => {
    const category = categories[categoryIndex];
    const value = category.monthlyBudgets[monthIndex] || 0;
    setFillSource({ categoryIndex, monthIndex, value });
    setFillDialogOpen(true);
  };

  const handleFillApply = (fillType: FillType, toMonth: number, horizontalDirection: 'right' | 'left' = 'right') => {
    if (!fillSource) return;

    const { categoryIndex, monthIndex, value } = fillSource;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];

    // Determine the range based on fill direction
    const startMonth = horizontalDirection === 'right' ? monthIndex : toMonth;
    const endMonth = horizontalDirection === 'right' ? toMonth : monthIndex;
    const monthCount = Math.abs(endMonth - startMonth) + 1;

    // Apply fill based on type
    if (fillType === 'copy') {
      // Copy same value to all months in range
      for (let i = startMonth; i <= endMonth; i++) {
        monthlyBudgets[i] = value;
      }
    } else if (fillType === 'distribute') {
      // Distribute value equally across months
      const valuePerMonth = value / monthCount;
      const roundedValue = Math.floor(valuePerMonth * 100) / 100;
      const remainder = value - (roundedValue * monthCount);
      
      for (let i = startMonth; i <= endMonth; i++) {
        const isLast = i === endMonth;
        monthlyBudgets[i] = isLast ? roundedValue + remainder : roundedValue;
      }
    }

    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };

    setCategories(updated);
    setFillDialogOpen(false);
    setFillSource(null);
  };

  // Handle pattern suggestion acceptance
  const handlePatternAccept = () => {
    if (!patternSuggestion) return;
    
    const { categoryIndex, value, fromMonth, toMonth } = patternSuggestion;
    const updated = [...categories];
    const monthlyBudgets = [...(updated[categoryIndex].monthlyBudgets || Array(totalMonths).fill(0))];
    
    // Fill remaining months with the pattern value
    for (let i = fromMonth; i <= toMonth; i++) {
      monthlyBudgets[i] = value;
    }
    
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets,
    };
    
    setCategories(updated);
    setPatternSuggestion(null);
  };
  
  const handlePatternDismiss = () => {
    if (patternSuggestion) {
      const suggestionKey = `pattern-${patternSuggestion.categoryIndex}`;
      setDismissedSuggestions(new Set(dismissedSuggestions).add(suggestionKey));
    }
    setPatternSuggestion(null);
  };

  // Duplicate handlers
  const handleDuplicateCategory = (categoryIndex: number) => {
    const sourceCategory = categories[categoryIndex];
    const newCategory: CategoryBudgetRow = {
      ...sourceCategory,
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: `${sourceCategory.name} (Copy)`,
      monthlyBudgets: [...sourceCategory.monthlyBudgets],
      scope: 'project', // Duplicated categories are project-specific
      isCustom: true,
    };

    // Insert below source category
    const updated = [
      ...categories.slice(0, categoryIndex + 1),
      newCategory,
      ...categories.slice(categoryIndex + 1),
    ];

    setCategories(updated);
  };

  const handleDuplicateMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const updatedCategories = [...categories];

    // Process in reverse order to maintain correct insertion positions
    for (let i = updatedCategories.length - 1; i >= 0; i--) {
      const category = updatedCategories[i];
      if (selectedCategoryIds.includes(category.id)) {
        const newCategory: CategoryBudgetRow = {
          ...category,
          id: Date.now() + Math.floor(Math.random() * 1000) + i,
          name: `${category.name} (Copy)`,
          monthlyBudgets: [...category.monthlyBudgets],
          scope: 'project',
          isCustom: true,
        };
        updatedCategories.splice(i + 1, 0, newCategory);
      }
    }

    setCategories(updatedCategories);
    setSelectedRows(new Set());
  };

  // Shift budget handler
  const handleShiftBudget = (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => {
    if (!shiftSource) return;

    const categoryIndex = shiftSource.categoryIndex;
    const category = categories[categoryIndex];
    const newMonthlyBudgets = [...(category.monthlyBudgets || Array(totalMonths).fill(0))];

    // Copy values to destination
    const shiftAmount = destinationStart - sourceFrom;
    const sourceMonthlyBudgets = category.monthlyBudgets || Array(totalMonths).fill(0);
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        newMonthlyBudgets[destIndex] = sourceMonthlyBudgets[i];
      }
    }

    // Clear source if requested
    if (clearSource) {
      const destinationEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        if (i < destinationStart || i > destinationEnd) {
          newMonthlyBudgets[i] = 0;
        }
      }
    }

    const updated = [...categories];
    updated[categoryIndex] = {
      ...updated[categoryIndex],
      monthlyBudgets: newMonthlyBudgets,
    };

    setCategories(updated);
    setShiftDialogOpen(false);
    setShiftSource(null);
  };

  // Delete handlers
  const removeCategory = (categoryIndex: number) => {
    const category = categories[categoryIndex];
    
    // Prevent deletion of global categories
    if (category.scope === 'global') {
      setDeleteAttempt({ index: categoryIndex, category });
      return;
    }

    // Allow deletion of project-specific categories
    setCategories(categories.filter((_, i) => i !== categoryIndex));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const removeMultipleCategories = () => {
    if (selectedRows.size === 0) return;

    const selectedCategoryIds = Array.from(selectedRows);
    const categoriesToDelete = categories.filter(cat => selectedCategoryIds.includes(cat.id));

    // Check if any selected categories have actual costs
    const categoriesWithCosts = categoriesToDelete.filter(cat => {
      // Check if there are any actual costs in the category
      const hasActualCosts = cat.actual > 0;
      // Also check if there are any monthly budgets allocated
      const hasMonthlyBudgets = cat.monthlyBudgets && cat.monthlyBudgets.some(budget => budget > 0);
      return hasActualCosts || hasMonthlyBudgets;
    });

    if (categoriesWithCosts.length > 0) {
      setDeleteAttempt({ index: -1, category: categoriesWithCosts[0] });
      return;
    }

    // Allow deletion of categories without costs
    setCategories(categories.filter(cat => !selectedCategoryIds.includes(cat.id)));
    setSelectedRows(new Set());
    setSelectedCell(null);
  };

  const cancelDeleteCategory = () => {
    setDeleteAttempt(null);
  };

  // Add Category handler (inline row, matching Resource Budget pattern)
  const addCategory = () => {
    const usedCategoryIds = categories.map(c => c.globalCategoryId).filter(Boolean);
    const availableCategory = availableGlobalCategories.find(
      gc => !usedCategoryIds.includes(gc.id)
    );

    const newCategory: CategoryBudgetRow = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: availableCategory?.name || '',
      type: availableCategory?.type || 'expenses',
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'project',
      isCustom: !availableCategory,
      globalCategoryId: availableCategory?.id,
      monthlyBudgets: Array(totalMonths).fill(0),
    };

    setCategories([...categories, newCategory]);
  };

  const updateCategorySelection = (index: number, globalCategoryId: string) => {
    if (!globalCategoryId) return;
    const globalCategory = availableGlobalCategories.find(gc => String(gc.id) === globalCategoryId);
    if (!globalCategory) return;

    const updated = [...categories];
    updated[index] = {
      ...updated[index],
      name: globalCategory.name,
      type: globalCategory.type,
      globalCategoryId: globalCategory.id,
      isCustom: false,
    };
    setCategories(updated);
  };

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
          <h2 className="text-lg font-semibold">Budget by Category</h2>
          <div className="flex items-center gap-2">
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const { categoryIndex, monthIndex } = selectedCell;
                  const category = categories[categoryIndex];
                  const value = category.monthlyBudgets?.[monthIndex] || 0;
                  // Validation: value > 0 && totalMonths > 1 (can fill in at least one direction)
                  if (value > 0 && totalMonths > 1) {
                    handleFillClick(categoryIndex, monthIndex);
                  }
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (() => {
                if (!selectedCell || selectedCell.monthIndex === null) return true;
                const { categoryIndex, monthIndex } = selectedCell;
                const category = categories[categoryIndex];
                if (!category) return true;
                const value = category.monthlyBudgets?.[monthIndex] || 0;
                // Disabled if: no value (value === 0) OR only one column (totalMonths === 1)
                return value === 0 || totalMonths === 1;
              })()}
              title="Fill from selected cell"
            >
              <ArrowLeftRight className="h-4 w-4 mr-2" />
              Fill
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) handleDuplicateCategory(categoryIndex);
                } else if (selectedRows.size > 1) {
                  handleDuplicateMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Duplicate ${selectedRows.size} selected categories` : "Duplicate category"}
            >
              <Files className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
            <Button 
              onClick={() => {
                if (selectedCell && selectedCell.monthIndex !== null) {
                  const categoryIndex = selectedCell.categoryIndex;
                  setShiftSource({ 
                    categoryIndex, 
                    categoryName: categories[categoryIndex].name 
                  });
                  setShiftDialogOpen(true);
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={!selectedCell || selectedCell.monthIndex === null || (categories[selectedCell.categoryIndex]?.monthlyBudgets?.[selectedCell.monthIndex] ?? 0) === 0}
              title="Shift budget between months (select a monthly cell first)"
            >
              <MoveHorizontal className="h-4 w-4 mr-2" />
              Shift
            </Button>
            <Button 
              onClick={() => {
                if (selectedRows.size === 1) {
                  const selectedCategoryId = Array.from(selectedRows)[0];
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategoryId);
                  if (categoryIndex !== -1) {
                    removeCategory(categoryIndex);
                  }
                } else if (selectedRows.size > 1) {
                  removeMultipleCategories();
                }
              }} 
              size="sm" 
              variant="outline"
              disabled={selectedRows.size === 0}
              title={selectedRows.size > 1 ? `Delete ${selectedRows.size} selected categories` : "Delete selected category"}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <Button 
              onClick={addCategory} 
              size="sm" 
              variant="outline"
              disabled={availableGlobalCategories.length > 0 && categories.filter(c => c.globalCategoryId).length >= availableGlobalCategories.length}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>
        </div>
        <div className="p-0">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {hasGlobalCategories === false ? (
                <div className="space-y-2">
                  <p>No budget categories configured.</p>
                  <p>
                    Please configure categories in{' '}
                    <button
                      type="button"
                      onClick={goToBudgetSettings}
                      className="text-primary underline underline-offset-4 hover:text-primary/80 inline-flex items-center gap-1 cursor-pointer"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Settings → Budget Settings
                    </button>
                  </p>
                </div>
              ) : (
                <>No categories yet. Click "Add Category" to get started.</>
              )}
            </div>
          ) : (
            <>
              {/* 3-Column Layout: Left (sticky) + Middle (scrollable) + Right (sticky) */}
              <div className="flex border rounded-md">
                {/* LEFT SECTION: Checkbox + Category (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-r">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px] sticky left-0 z-10 bg-background">
                          <Checkbox
                            checked={selectedRows.size === categories.length && categories.length > 0}
                            onCheckedChange={toggleAllRows}
                            aria-label="Select all rows"
                          />
                        </TableHead>
                        <TableHead className="w-[200px] sticky left-[40px] z-10 border-r bg-background">Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category, index) => {
                        const isSelected = selectedRows.has(category.id);
                        const isCellSelected = selectedCell?.categoryIndex === index;
                        const isLabour = category.type === 'labour';
                        
                        return (
                          <TableRow 
                            key={category.id}
                            className={cn(
                              'group',
                              isCellSelected && 'bg-primary/5',
                              isSelected && 'bg-primary/10'
                            )}
                          >
                            <TableCell className={cn(
                              'p-2 w-[40px] sticky left-0 z-10',
                              isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'
                            )}>
                              <div className="flex items-center h-9">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleRowSelection(category.id)}
                                  aria-label={`Select ${category.name}`}
                                />
                              </div>
                            </TableCell>
                            <TableCell className={cn(
                              'p-2 w-[200px] sticky left-[40px] z-10 border-r',
                              isCellSelected ? 'bg-primary/5' : isSelected ? 'bg-primary/10' : 'bg-background'
                            )}>
                              {isLabour ? (
                                <div className="flex items-center gap-2 h-9 px-2">
                                  <span className="font-medium text-muted-foreground">{category.name}</span>
                                  <span className="text-xs text-muted-foreground">(Auto)</span>
                                </div>
                              ) : (
                                <Popover
                                  open={openCategoryPopover === index}
                                  onOpenChange={(open) => setOpenCategoryPopover(open ? index : null)}
                                >
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      aria-expanded={openCategoryPopover === index}
                                      className={cn(
                                        "w-full justify-between h-9",
                                        !category.name && "text-muted-foreground"
                                      )}
                                    >
                                      <span className="truncate">{category.name || "Select category"}</span>
                                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                    <Command shouldFilter={true} filter={(value, search) => value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0}>
                                      <CommandInput placeholder="Search categories..." className="h-9" />
                                      <CommandList>
                                        <CommandEmpty>No category found.</CommandEmpty>
                                        {(() => {
                                          const usedIds = categories
                                            .filter((c, i) => i !== index)
                                            .map(c => c.globalCategoryId)
                                            .filter(Boolean);
                                          const available = availableGlobalCategories.filter(gc => !usedIds.includes(gc.id));
                                          if (category.globalCategoryId && !available.some(gc => gc.id === category.globalCategoryId)) {
                                            const current = availableGlobalCategories.find(gc => gc.id === category.globalCategoryId);
                                            if (current) available.unshift(current);
                                          }
                                          const groups = CATEGORY_TYPE_ORDER
                                            .map(type => ({
                                              type,
                                              label: CATEGORY_TYPE_LABELS[type] || type,
                                              items: available.filter(gc => gc.type === type),
                                            }))
                                            .filter(g => g.items.length > 0);
                                          return groups.map((group, groupIndex) => (
                                            <div key={group.type}>
                                              {groupIndex > 0 && <CommandSeparator />}
                                              <CommandGroup
                                                heading={group.label}
                                                className="**:[[cmdk-group-heading]]:uppercase **:[[cmdk-group-heading]]:tracking-wider **:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:border-b **:[[cmdk-group-heading]]:border-border **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:mb-1"
                                              >
                                                {group.items.map(gc => (
                                                  <CommandItem
                                                    key={gc.id}
                                                    value={gc.name}
                                                    onSelect={() => {
                                                      updateCategorySelection(index, String(gc.id));
                                                      setOpenCategoryPopover(null);
                                                    }}
                                                    className="pl-3"
                                                  >
                                                    <div className="flex-1 min-w-0">{gc.name}</div>
                                                    <Check
                                                      className={cn(
                                                        "h-4 w-4 shrink-0",
                                                        category.globalCategoryId === gc.id ? "opacity-100" : "opacity-0"
                                                      )}
                                                    />
                                                  </CommandItem>
                                                ))}
                                              </CommandGroup>
                                            </div>
                                          ));
                                        })()}
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[40px] sticky left-0 z-10 bg-background"></TableCell>
                        <TableCell className="p-2 w-[200px] sticky left-[40px] z-10 border-r font-semibold bg-background">Total</TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* MIDDLE SECTION: Monthly Columns (Scrollable) */}
                <div 
                  className="flex-1 overflow-x-auto overflow-y-visible [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-slate-500"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(203 213 225) rgb(241 245 249)',
                  }}
                >
                  <Table 
                    className="border-collapse"
                    style={{
                      // Make table width match the number of months (100px per month)
                      width: `${yearMonths.length * 100}px`,
                      minWidth: '100%'
                    }}
                  >
                    <TableHeader>
                      <TableRow>
                        {yearMonths.map((monthData) => (
                          <TableHead key={`${monthData.year}-${monthData.month}`} className="w-[100px] text-center">
                            {MONTHS[monthData.month]}/{String(monthData.year).slice(-2)}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category, index) => {
                        const isLabour = category.type === 'labour';
                        const isDisabled = isLabour;
                        
                        return (
                          <TableRow key={category.id}>
                            {visibleMonths.map((monthIndex) => {
                              // Ensure monthlyBudgets array is the correct size
                              const existingBudgets = category.monthlyBudgets || [];
                              const monthlyBudgets = Array(totalMonths).fill(0).map((_, i) => existingBudgets[i] || 0);
                              const isSelected = selectedCell?.categoryIndex === index && selectedCell?.monthIndex === monthIndex;

                              return (
                                <TableCell key={monthIndex} className="p-2 w-[100px]">
                                  <NumberInput
                                    value={monthlyBudgets[monthIndex]}
                                    onChange={(value) => updateMonthlyBudget(index, monthIndex, value)}
                                    maxValue={999999.99}
                                    onFocus={() => setSelectedCell({ categoryIndex: index, monthIndex })}
                                    disabled={isDisabled}
                                    className={cn(
                                      "w-full h-9 px-2 text-sm text-center",
                                      isSelected && "ring-2 ring-ring",
                                      isDisabled && "bg-muted cursor-not-allowed"
                                    )}
                                  />
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        {visibleMonths.map((monthIndex) => {
                          const monthTotal = categories.reduce((sum, category) => {
                            const existingBudgets = category.monthlyBudgets || [];
                            const budget = existingBudgets[monthIndex] || 0;
                            return sum + budget;
                          }, 0);
                          return (
                            <TableCell key={monthIndex} className="p-2 w-[100px]">
                              <Input
                                value={formatCurrency(monthTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-muted cursor-default"
                                title={formatCurrency(monthTotal)}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* RIGHT SECTION: Project Total (Fixed, No Scroll) */}
                <div className="flex-shrink-0 border-l-2">
                  <Table className="border-collapse">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[120px] text-center font-semibold bg-primary/10">Project Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category) => {
                        const categoryTotal = category.monthlyBudgets.reduce((sum, val) => sum + val, 0);
                        
                        return (
                          <TableRow key={category.id}>
                            <TableCell className="p-2 w-[120px]">
                              <Input
                                value={formatCurrency(categoryTotal)}
                                readOnly
                                className="w-full h-9 px-2 text-sm text-center font-semibold bg-primary/10 cursor-default"
                                title={formatCurrency(categoryTotal)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell className="p-2 w-[120px] bg-background">
                          <Input
                            value={formatCurrency(categories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0))}
                            readOnly
                            className="w-full h-9 px-2 text-sm text-center font-bold bg-primary/20 cursor-default"
                            title={formatCurrency(categories.reduce((sum, cat) => sum + cat.monthlyBudgets.reduce((s, v) => s + v, 0), 0))}
                          />
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Pattern Suggestion */}
      {patternSuggestion && (
        <AISuggestion
          type="info"
          title="Pattern Detected"
          message={`Fill remaining months with ${formatCurrency(patternSuggestion.value)}?`}
          details={`You've entered ${formatCurrency(patternSuggestion.value)} for 3 consecutive months. Would you like to fill ${MONTHS[yearMonths[patternSuggestion.fromMonth]?.month]} through ${MONTHS[yearMonths[patternSuggestion.toMonth]?.month]} with the same value?`}
          confidence={95}
          onAccept={handlePatternAccept}
          onDismiss={handlePatternDismiss}
          acceptLabel="Fill Remaining Months"
          dismissLabel="No Thanks"
        />
      )}

      {/* Fill Sheet */}
      <FillSheet
        open={fillDialogOpen}
        onOpenChange={setFillDialogOpen}
        onApply={handleFillApply}
        sourceMonth={fillSource?.monthIndex ?? 0}
        totalMonths={totalMonths}
        sourceValue={fillSource?.value ?? 0}
        direction="right"
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
        budgetType="cost"
      />

      {/* Shift Budget Sheet */}
      <ShiftBudgetSheet
        open={shiftDialogOpen}
        onOpenChange={setShiftDialogOpen}
        onShift={handleShiftBudget}
        totalMonths={totalMonths}
        monthLabels={yearMonths.map((ym) => `${MONTHS[ym.month]} ${ym.year}`)}
        roleName={shiftSource?.categoryName ?? ''}
        monthlyHours={shiftSource ? categories[shiftSource.categoryIndex]?.monthlyBudgets ?? [] : []}
        selectedMonthIndex={selectedCell?.monthIndex ?? undefined}
        budgetType="cost"
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteCategory()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Delete Category with Costs</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                The category <strong>"{deleteAttempt?.category.name}"</strong> cannot be deleted because it has budget or actual costs allocated.
              </p>
              <p className="text-sm">
                To remove this category from your project:
              </p>
              <ul className="text-sm list-disc list-inside space-y-1 ml-2">
                <li>Clear all monthly budget values (set them to zero)</li>
                <li>Ensure there are no actual costs recorded</li>
                <li>Then try deleting the category again</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                This prevents accidental deletion of categories with financial data.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={cancelDeleteCategory}>
              I Understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
